import { StatoArticolo } from './stato-articolo.util';

export interface FiltriArticoli {
  id?: number | null;
  titolo?: string | null;
  stato?: StatoArticolo['codice'] | null;
  categoriaId?: number | null;
  autoreId?: number | null;
  /** Estremi del periodo, in secondi. */
  da?: number | null;
  a?: number | null;
}

/**
 * Traduce i filtri della barra nelle condizioni che il backend capisce.
 *
 * Sta qui, fuori dal componente, perche' e' la parte che vale la pena provare:
 * gli operatori accettati dal CRUD sono `$like`, `$between`, `$gt`, `$lt`,
 * `$gte`, `$lte`, `$in`, `$or`.
 *
 * Il punto delicato e' che stato e periodo filtrano entrambi la colonna
 * `data`: scritti uno sopra l'altro, uno dei due sparirebbe in silenzio.
 */
export function condizioniArticoli(
  filtri: FiltriArticoli,
  adessoInSecondi: number = Math.floor(Date.now() / 1000)
): Record<string, unknown> {
  const q: Record<string, unknown> = {};

  if (filtri.id) q['id'] = filtri.id;

  const titolo = filtri.titolo?.trim();
  if (titolo) q['titolo'] = { $like: titolo };

  if (filtri.categoriaId) q['categoria'] = { id: filtri.categoriaId };
  if (filtri.autoreId) q['autore'] = { id: filtri.autoreId };

  if (filtri.stato === 'bozza') q['approvato'] = '0';
  if (filtri.stato === 'pubblicato' || filtri.stato === 'programmato') q['approvato'] = '1';

  const data = condizioneData(filtri, adessoInSecondi);
  if (data) q['data'] = data;

  return q;
}

function condizioneData(filtri: FiltriArticoli, adesso: number): Record<string, unknown> | null {
  // Lo stato restringe il periodo invece di sostituirlo.
  let da = filtri.da ?? null;
  let a = filtri.a ?? null;
  const senzaPeriodo = da === null && a === null;

  if (filtri.stato === 'pubblicato') {
    // Senza periodo basta il limite superiore, inclusivo: "gia' uscito".
    if (senzaPeriodo) return { $lte: adesso };
    a = a === null ? adesso : Math.min(a, adesso);
  } else if (filtri.stato === 'programmato') {
    // "ancora da uscire": adesso escluso, quindi `$gt` da solo e un limite
    // inferiore spostato di un secondo quando si combina con un periodo.
    if (senzaPeriodo) return { $gt: adesso };
    da = da === null ? adesso + 1 : Math.max(da, adesso + 1);
  }

  if (da !== null && a !== null) {
    // Intervallo vuoto: succede quando si chiede "programmato" in un periodo
    // tutto passato. Meglio la condizione dello stato da sola, che non
    // restituira' niente, di un intervallo rovesciato che il database
    // interpreta a modo suo.
    if (da > a) {
      if (filtri.stato === 'programmato') return { $gt: adesso };
      if (filtri.stato === 'pubblicato') return { $lte: adesso };
      return null;
    }
    return { $between: [da, a] };
  }

  if (da !== null) return { $gte: da };
  if (a !== null) return { $lte: a };

  return null;
}
